import type { Ref } from 'vue'

import type { ArrivalBeat } from '@/lib/arrival/arrival-schedule'
import {
  HANDOVER_REDUCED_MS,
  type HandoverCast,
  type HandoverSchedule,
  handoverSchedule,
} from '@/lib/handover/handover-schedule'
import { blurIn, blurOut, FADE_OUT, pinInPlace, settled } from '@/lib/motion/motion-keyframes'

import { type MotionSequence, useMotionSequence } from './useMotionSequence'

type HandoverPart =
  | 'step'
  | 'rail'
  | 'nav-row'
  | 'tile'
  | 'content'
  | 'create'
  | 'hub'
  | 'dock-button'

/** Plays the handover: the setup screen and the setup steps leaving, then Home arriving in their place. */
export interface HandoverSequence {
  /** True when the founder asked for reduced motion; the leaving parts then fade and the arriving ones are simply present. */
  readonly reduced: Readonly<Ref<boolean>>
  /** Pins the setup screen where it stands and plays its exit. Resolves once the screen has left. */
  leaveScreen(screen: HTMLElement): Promise<void>
  /** Pins the setup step list where it stands and lifts each step row, and the rail below it, away. Resolves once the list has left. */
  leaveSteps(stepList: HTMLElement): Promise<void>
  /** Plays the arrival of the nav rows, Home's content and the three floating controls. Resolves once every part has landed. */
  arrive(shell: Element): Promise<void>
}

/** The spacing tokens the keyframes are measured in, read once per play. */
interface HandoverTokens {
  /** The blur and nudge of the Create button. */
  readonly small: string
  /** The blur of the nav rows, the hub and the chat button, and the rise of every row and part. */
  readonly medium: string
  /** The blur of Home's content and of the leaving screen. */
  readonly large: string
}

const EXIT_SCALE = 0.985
const NO_ROWS: HandoverCast = { stepRows: 0, navRows: 0, contentParts: 0 }

function selector(part: HandoverPart): string {
  return `[data-arrival="${part}"]`
}

function one(root: Element | null, part: HandoverPart): Element | null {
  return root?.querySelector(selector(part)) ?? null
}

function every(root: Element | null, part: HandoverPart): Element[] {
  return root === null ? [] : Array.from(root.querySelectorAll(selector(part)))
}

function readTokens(motion: MotionSequence): HandoverTokens {
  return {
    small: motion.token('--spacing-2'),
    medium: motion.token('--spacing-4'),
    large: motion.token('--spacing-6'),
  }
}

/** Plays the handover from `lib/handover`'s schedule; leaving parts hold their last keyframe until removed, arriving parts animate toward their resting style. */
export function useHandoverSequence(): HandoverSequence {
  const motion = useMotionSequence()

  function playEach(
    elements: readonly (Element | null)[],
    beats: readonly (ArrivalBeat | null | undefined)[],
    keyframes: Keyframe[],
  ): (Animation | null)[] {
    return elements.map((element, index) => {
      const beat = beats[index]
      return beat ? motion.play(element, keyframes, beat) : null
    })
  }

  function reducedFade(element: HTMLElement): Promise<void> {
    return settled([
      motion.play(element, FADE_OUT, {
        duration: HANDOVER_REDUCED_MS,
        easing: 'standard',
        fill: 'both',
      }),
    ])
  }

  async function leaveScreen(screen: HTMLElement): Promise<void> {
    pinInPlace(screen)
    if (motion.reduced.value) return reducedFade(screen)
    const { screen: beat } = handoverSchedule(NO_ROWS)
    const keyframes = blurOut(readTokens(motion).large, `scale(${EXIT_SCALE})`)
    await settled([motion.play(screen, keyframes, { ...beat, fill: 'both' })])
  }

  async function leaveSteps(stepList: HTMLElement): Promise<void> {
    pinInPlace(stepList)
    if (motion.reduced.value) return reducedFade(stepList)
    const steps = every(stepList, 'step')
    const { steps: beats } = handoverSchedule({ ...NO_ROWS, stepRows: steps.length })
    const { medium } = readTokens(motion)
    const keyframes = blurOut(medium, `translateY(calc(${medium} * -1))`)
    const lifting = (element: Element, beat: ArrivalBeat | undefined): Animation | null =>
      beat ? motion.play(element, keyframes, { ...beat, fill: 'both' }) : null
    await settled([
      ...steps.map((step, index) => lifting(step, beats[index])),
      ...every(stepList, 'rail').map((rail, index) => lifting(rail, beats[index])),
    ])
  }

  function playNav(
    shell: Element,
    schedule: HandoverSchedule,
    tokens: HandoverTokens,
  ): (Animation | null)[] {
    return [
      ...playEach(
        every(shell, 'nav-row'),
        schedule.navRows,
        blurIn(tokens.medium, `translateY(${tokens.medium})`),
      ),
      motion.play(
        one(shell, 'create'),
        blurIn(tokens.small, `translateX(calc(${tokens.small} * -1))`),
        schedule.create,
      ),
      motion.play(one(shell, 'hub'), blurIn(tokens.medium), schedule.hub),
    ]
  }

  function playTile(
    shell: Element,
    schedule: HandoverSchedule,
    tokens: HandoverTokens,
  ): (Animation | null)[] {
    return [
      ...playEach(
        every(one(shell, 'tile'), 'content'),
        schedule.content,
        blurIn(tokens.large, `translateY(${tokens.medium})`),
      ),
      motion.play(one(shell, 'dock-button'), blurIn(tokens.medium), schedule.dockButton),
    ]
  }

  async function arrive(shell: Element): Promise<void> {
    if (motion.reduced.value) return
    const schedule = handoverSchedule({
      stepRows: 0,
      navRows: every(shell, 'nav-row').length,
      contentParts: every(one(shell, 'tile'), 'content').length,
    })
    const tokens = readTokens(motion)
    await settled([...playNav(shell, schedule, tokens), ...playTile(shell, schedule, tokens)])
  }

  return { reduced: motion.reduced, leaveScreen, leaveSteps, arrive }
}
